import { useRef, useState } from 'react';
import { Modal } from './Modal';
import { AsyncButton } from '@/components/common/AsyncButton';

interface FileUploadDialogProps {
  show: boolean;
  title: string;
  accept?: string;
  confirmLabel?: string;
  confirmIcon?: string;
  // Validation message when confirm is pressed with no file selected.
  noFileMessage?: string;
  onClose: () => void;
  // Called with the chosen file; may return a promise (button shows a loader).
  onUpload: (file: File) => void | Promise<unknown>;
}

// Controlled modal with a single file input for Excel uploads (reuse-ready).
// The caller's onUpload performs the multipart POST (see api/client/download.ts
// uploadFile), so this stays transport-agnostic.
export function FileUploadDialog({
  show,
  title,
  accept = '.xlsx,.xls',
  confirmLabel = 'Upload',
  confirmIcon = 'fa fa-upload',
  noFileMessage = 'Please select a file',
  onClose,
  onUpload,
}: FileUploadDialogProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setFile(null);
    setError(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  const close = () => {
    reset();
    onClose();
  };

  const confirm = async () => {
    if (!file) {
      setError(noFileMessage);
      return;
    }
    await onUpload(file);
  };

  return (
    <Modal
      show={show}
      title={title}
      onClose={close}
      footer={
        <>
          <button type="button" className="btn btn-light" onClick={close}>
            Cancel
          </button>
          <AsyncButton className="btn btn-primary" icon={confirmIcon} onClick={confirm}>
            {confirmLabel}
          </AsyncButton>
        </>
      }
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className={`form-control${error ? ' is-invalid' : ''}`}
        onChange={(e) => {
          setFile(e.target.files?.[0] ?? null);
          setError(null);
        }}
      />
      {error && <div className="invalid-feedback d-block">{error}</div>}
    </Modal>
  );
}
